import { ref, type Ref, toValue } from 'vue';
import * as Cesium from 'cesium';

export type MeasurementMode = 'distance' | 'area' | null;

export interface DistanceResult {
    total: number;
    horizontal: number;
    vertical: number;
}

export interface Measurement {
    type: 'distance' | 'area';
    positions: Cesium.Cartesian3[];
    value?: number;
    area?: number;
    perimeter?: number;
}

export interface MeasurementComposable {
    measurementMode: Ref<MeasurementMode>;
    activePoints: Ref<Cesium.Cartesian3[]>;
    measurements: Ref<Measurement[]>;
    startMeasurement: (mode: 'distance' | 'area') => void;
    stopMeasurement: () => void;
    clearAll: () => void;
    cleanup: () => void;
    formatDistance: (meters: number) => string;
    formatArea: (squareMeters: number) => string;
    calculateDistance: (pos1: Cesium.Cartesian3, pos2: Cesium.Cartesian3) => DistanceResult;
    calculatePolygonArea: (positions: Cesium.Cartesian3[]) => number;
}

export function useCesiumMeasurement(viewerRef: Ref<Cesium.Viewer | null>, jakartoPointcloudsRef: Ref<Cesium.Cesium3DTileset | null>): MeasurementComposable {
    const ellipsoid = Cesium.Ellipsoid.WGS84;
    const geodesic = new Cesium.EllipsoidGeodesic();

    const measurementMode = ref<MeasurementMode>(null);
    const activePoints = ref<Cesium.Cartesian3[]>([]);
    const measurements = ref<Measurement[]>([]);

    let pointCollection: Cesium.PointPrimitiveCollection | null = null;
    let polylineCollection: Cesium.PolylineCollection | null = null;
    let handler: Cesium.ScreenSpaceEventHandler | null = null;
    const entities: Cesium.Entity[] = [];

    const getViewer = (): Cesium.Viewer | null => {
        return toValue(viewerRef);
    };

    const initCollections = (): boolean => {
        const viewer = getViewer();
        if (!viewer || !viewer.scene) {
            return false;
        }

        if (!pointCollection) {
            pointCollection = viewer.scene.primitives.add(
                new Cesium.PointPrimitiveCollection()
            );
        }
        if (!polylineCollection) {
            polylineCollection = viewer.scene.primitives.add(
                new Cesium.PolylineCollection()
            );
        }
        return true;
    };

    const calculateDistance = (
        pos1: Cesium.Cartesian3,
        pos2: Cesium.Cartesian3
    ): DistanceResult => {
        const cart1 = Cesium.Cartographic.fromCartesian(pos1);
        const cart2 = Cesium.Cartographic.fromCartesian(pos2);

        geodesic.setEndPoints(cart1, cart2);
        const horizontal = geodesic.surfaceDistance;

        const vertical = Math.abs(cart2.height - cart1.height);

        const total = Math.sqrt(horizontal * horizontal + vertical * vertical);

        return { total, horizontal, vertical };
    };

    const calculatePathDistance = (positions: Cesium.Cartesian3[]): number => {
        let totalDistance = 0;
        for (let i = 0; i < positions.length - 1; i++) {
            const dist = calculateDistance(positions[i], positions[i + 1]);
            totalDistance += dist.total;
        }
        return totalDistance;
    };

    const calculatePolygonArea = (positions: Cesium.Cartesian3[]): number => {
        if (positions.length < 3) return 0;

        const cartographics = positions.map(pos =>
            Cesium.Cartographic.fromCartesian(pos)
        );

        let area = 0;
        for (let i = 0; i < cartographics.length; i++) {
            const j = (i + 1) % cartographics.length;

            const lon1 = cartographics[i].longitude;
            const lat1 = cartographics[i].latitude;
            const lon2 = cartographics[j].longitude;
            const lat2 = cartographics[j].latitude;

            area += (lon2 - lon1) * (2 + Math.sin(lat1) + Math.sin(lat2));
        }

        area = Math.abs(area * ellipsoid.maximumRadius * ellipsoid.maximumRadius / 2);
        return area;
    };

    const formatDistance = (meters: number): string => {
        if (meters >= 1000) {
            return `${(meters / 1000).toFixed(2)} km`;
        }
        return `${meters.toFixed(2)} m`;
    };

    const formatArea = (squareMeters: number): string => {
        if (squareMeters >= 1000000) {
            return `${(squareMeters / 1000000).toFixed(2)} km²`;
        } else if (squareMeters >= 10000) {
            return `${(squareMeters / 10000).toFixed(2)} ha`;
        }
        return `${squareMeters.toFixed(2)} m²`;
    };

    const getMidpoint = (
        pos1: Cesium.Cartesian3,
        pos2: Cesium.Cartesian3
    ): Cesium.Cartesian3 => {
        const cart1 = Cesium.Cartographic.fromCartesian(pos1);
        const cart2 = Cesium.Cartographic.fromCartesian(pos2);

        geodesic.setEndPoints(cart1, cart2);
        const midCart = geodesic.interpolateUsingFraction(0.5, new Cesium.Cartographic());

        const height = (cart1.height + cart2.height) / 2;
        return Cesium.Cartesian3.fromRadians(midCart.longitude, midCart.latitude, height);
    };

    const getCentroid = (positions: Cesium.Cartesian3[]): Cesium.Cartesian3 => {
        const cartographics = positions.map(pos =>
            Cesium.Cartographic.fromCartesian(pos)
        );

        const avgLon = cartographics.reduce((sum, c) => sum + c.longitude, 0) / cartographics.length;
        const avgLat = cartographics.reduce((sum, c) => sum + c.latitude, 0) / cartographics.length;
        const avgHeight = cartographics.reduce((sum, c) => sum + c.height, 0) / cartographics.length;

        return Cesium.Cartesian3.fromRadians(avgLon, avgLat, avgHeight);
    };

    const addPoint = (
        position: Cesium.Cartesian3,
        color: Cesium.Color = Cesium.Color.YELLOW
    ): Cesium.PointPrimitive => {
        if (!pointCollection) {
            throw new Error('Point collection not initialized');
        }

        return pointCollection.add({
            position: position,
            color: color,
            pixelSize: 10,
            outlineColor: Cesium.Color.BLACK,
            outlineWidth: 2,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            heightReference: Cesium.HeightReference.NONE
        });
    };

    const addPolyline = (
        positions: Cesium.Cartesian3[],
        width: number = 3
    ): Cesium.Polyline => {
        if (!polylineCollection) {
            throw new Error('Polyline collection not initialized');
        }

        return polylineCollection.add({
            positions: positions,
            width: width,
            material: new Cesium.Material({
                fabric: {
                    type: 'Color',
                    uniforms: { color: Cesium.Color.YELLOW }
                }
            }),
            clampToGround: false
        });
    };

    const addLabel = (
        position: Cesium.Cartesian3,
        text: string,
        offset: Cesium.Cartesian2 = new Cesium.Cartesian2(0, -20)
    ): Cesium.Entity | null => {
        const viewer = getViewer();
        if (!viewer) return null;

        const label = viewer.entities.add({
            position: position,
            label: {
                text: text,
                font: '14px monospace',
                showBackground: true,
                backgroundColor: Cesium.Color.BLACK.withAlpha(0.7),
                horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
                verticalOrigin: Cesium.VerticalOrigin.CENTER,
                pixelOffset: offset,
                fillColor: Cesium.Color.WHITE,
                disableDepthTestDistance: Number.POSITIVE_INFINITY
            }
        });
        entities.push(label);
        return label;
    };

    const completeDistanceMeasurement = (): void => {
        const viewer = getViewer();
        if (!viewer) return;

        if (activePoints.value.length !== 2) return;

        const [pos1, pos2] = activePoints.value;
        const distances = calculateDistance(pos1, pos2);

        addPolyline([pos1, pos2]);

        const midpoint = getMidpoint(pos1, pos2);
        addLabel(midpoint, formatDistance(distances.total));

        measurements.value.push({
            type: 'distance',
            positions: [...activePoints.value],
            value: distances.total
        });

        activePoints.value = [];
        viewer.scene.requestRender();
    };

    const completeAreaMeasurement = (): void => {
        if (activePoints.value.length < 3) return;

        const viewer = getViewer();
        if (!viewer) return;

        const positions = [...activePoints.value];
        const area = calculatePolygonArea(positions);
        const perimeter = calculatePathDistance([...positions, positions[0]]);

        const polygon = viewer.entities.add({
            polygon: {
                hierarchy: new Cesium.PolygonHierarchy(positions),
                material: Cesium.Color.YELLOW.withAlpha(0.3),
                outline: true,
                outlineColor: Cesium.Color.YELLOW,
                outlineWidth: 2
            }
        });
        entities.push(polygon);

        const centroid = getCentroid(positions);
        addLabel(centroid, `${formatArea(area)}\n${formatDistance(perimeter)}`);

        measurements.value.push({
            type: 'area',
            positions: positions,
            area: area,
            perimeter: perimeter
        });

        activePoints.value = [];
        viewer.scene.requestRender();
    };

    const handleClick = (click: Cesium.ScreenSpaceEventHandler.PositionedEvent): void => {
        if (!measurementMode.value) return;

        const viewer = getViewer();
        if (!viewer) return;

        const scene = viewer.scene;
        if (scene.mode === Cesium.SceneMode.MORPHING) return;

        const pickedObject = scene.pick(click.position);

        if (!Cesium.defined(pickedObject)) return;

        if (pickedObject.primitive !== jakartoPointcloudsRef.value &&
            pickedObject.tileset !== jakartoPointcloudsRef.value) {
            return;
        }

        const cartesian = scene.pickPosition(click.position);
        if (!Cesium.defined(cartesian)) return;

        addPoint(cartesian)
        viewer.scene.requestRender();
        activePoints.value.push(cartesian);

        if (measurementMode.value === 'distance' && activePoints.value.length === 2) {
            completeDistanceMeasurement();
        } else if (measurementMode.value === 'area' && activePoints.value.length > 1) {
            const positions = activePoints.value;
            addPolyline(
                [positions[positions.length - 2], positions[positions.length - 1]],
                2
            );
            viewer.scene.requestRender();
        }
    };

    const handleRightClick = (): void => {
        if (measurementMode.value === 'area' && activePoints.value.length >= 3) {
            completeAreaMeasurement();
            stopMeasurement();
        }
    };

    const setupHandlers = (): void => {
        const viewer = getViewer();
        if (!viewer || !viewer.scene) {
            return;
        }

        if (handler) {
            handler.destroy();
        }

        handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
        handler.setInputAction(handleClick, Cesium.ScreenSpaceEventType.LEFT_CLICK);
        handler.setInputAction(handleRightClick, Cesium.ScreenSpaceEventType.RIGHT_CLICK);
    };

    let cleanupKeyboard: (() => void) | null = null;

    const removeHandlers = (): void => {
        if (handler) {
            handler.destroy();
            handler = null;
        }

        if (cleanupKeyboard) {
            cleanupKeyboard();
            cleanupKeyboard = null;
        }
    };

    const startMeasurement = (mode: 'distance' | 'area'): void => {
        const viewer = getViewer();
        if (!viewer || !viewer.scene) {
            console.warn('Viewer not initialized yet');
            return;
        }

        if (!initCollections()) return;
        measurementMode.value = mode;
        activePoints.value = [];
        setupHandlers();
    };

    const stopMeasurement = (): void => {
        activePoints.value = [];
        removeHandlers();
    };

    const clearAll = (): void => {
        stopMeasurement();

        if (pointCollection) {
            pointCollection.removeAll();
        }
        if (polylineCollection) {
            polylineCollection.removeAll();
        }

        const viewer = getViewer();
        if (viewer) {
            entities.forEach(entity => {
                viewer.entities.remove(entity);
            });
            viewer.scene.requestRender();
        }
        entities.length = 0;

        measurements.value = [];
    };

    const cleanup = (): void => {
        clearAll();
    };

    return {
        measurementMode,
        activePoints,
        measurements,
        startMeasurement,
        stopMeasurement,
        clearAll,
        cleanup,
        formatDistance,
        formatArea,
        calculateDistance,
        calculatePolygonArea
    };
}